import axios from "axios";
import { toast } from "@/hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30000,
});

// Endpoints propios de auth: nunca deben disparar el flujo de refresh/redirect
// (login maneja su propio error; refresh se llama con axios "pelado"
// para no generar un loop infinito de interceptores).
const AUTH_NO_INTERCEPT = ["/auth/login", "/auth/refresh"];

let refreshPromise = null;

function requestRefresh() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API}/auth/refresh`, {}, { withCredentials: true })
      .then(() => true)
      .catch(() => false)
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const url = original.url || "";
    const status = error.response?.status;
    const isAuthEndpoint = AUTH_NO_INTERCEPT.some((p) => url.includes(p));

    if (error.code === "ECONNABORTED") {
      // Fix H: un timeout no trae response; antes se escribia .data sobre undefined, saltaba un TypeError y la pantalla
      // mostraba "Algo salio mal. Intenta de nuevo." en lugar de este mensaje.
      error.response = { ...(error.response || {}), data: { detail: "La solicitud tardo demasiado en responder. Revisa tu conexion e intenta de nuevo." } };
      return Promise.reject(error);
    }

    if (status === 401 && !isAuthEndpoint && !original._sessionRetried) {
      original._sessionRetried = true;
      const refreshed = await requestRefresh();
      if (refreshed) {
        return api(original);
      }
      // Sesion realmente vencida (o nunca existio, ej. /auth/me al entrar sin
      // login). Solo avisamos + redirigimos si no estamos ya en una pantalla
      // publica/de login (evita el aviso al cargar /login por primera vez).
      const isSilentCheck = url.includes("/auth/me");
      const onPublicScreen = typeof window !== "undefined" && (
        window.location.pathname.startsWith("/login") ||
        window.location.pathname.startsWith("/inscripcion")
      );
      if (error.response) {
        error.response.data = { ...(error.response.data || {}), detail: "Tu sesion expiro. Inicia sesion de nuevo para continuar." };
      }
      if (!isSilentCheck && !onPublicScreen) {
        toast({ title: "Tu sesion expiro", description: "Te llevamos al login para que inicies sesion de nuevo.", variant: "destructive" });
        setTimeout(() => { window.location.href = "/login"; }, 1200);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salio mal. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function getFileUrl(path) {
  if (!path) return null;
  return `${BACKEND_URL}${path}`;
}

export function getPublicFileUrl(path) {
  if (!path) return null;
  const publicPath = path.replace('/api/files/', '/api/public/files/');
  return `${BACKEND_URL}${publicPath}`;
}

export default api;
